// ============================================================
// ReportBilling — ใบแจ้งหนี้ทั้งอาคาร
// ------------------------------------------------------------
// สร้างเอกสาร PDF ด้วย pdfmake แล้วเปิดในแท็บใหม่ด้วย openPdf()
// ตั้งค่า pdfmake + ฟอนต์ไทย อยู่ที่ documents/pdf.js (ใช้ร่วมกันทุกไฟล์ในโฟลเดอร์นี้)
// ============================================================

import { openPdf } from "../pdf.js";
import { loading, toFixed, format_date, getStorage, replaceText } from "../../shared/index.js";

import "../../assets/css/bootstrap.css";
import "../../shared/config/global.js";

// เรียกปิด loading ทันทีตั้งแต่เข้ามาโหลดหน้า/ไฟล์นี้
loading(false);

// ใบแจ้งหนี้ตามอาคาร
async function report_billing(data, current_month, last_month) {
  loading(true); // เปิด loading เริ่มประมวลผล PDF

  // จัดกลุ่มข้อมูลตามอาคารรอบเดียว (O(N)) เร็วกว่าการใช้ filter ซ้อน map มาก
  const groupedByBuilding = data.reduce((acc, item) => {
    let bd = item.building || "-";
    if (!acc[bd]) acc[bd] = [];
    acc[bd].push(item);
    return acc;
  }, {});

  const buildingKeys = Object.keys(groupedByBuilding);
  
  let text_array = buildingKeys.map((bd, bd_index) => {
    const tableBody = groupedByBuilding[bd].map((item) => {
      let other = (item.other_income || 0) + (item.fire_cost || 0) + (item.cable_cost || 0);
      let debt = (item.add_debt || 0) - (item.reduce_debt || 0);
      let totalAmt = (item.total_amount || 0) + (item.overdue_amount || 0);

      return [
        // ห้องที่เปลี่ยนเจ้าของจะมีหลายแถวในบ้านเลขที่เดียวกัน (จัดกลุ่มตาม address_number + ref_customer_id)
        // จึงต้องโชว์ชื่อลูกหนี้คู่กับบ้านเลขที่ ไม่งั้นแยกไม่ออกว่าแถวไหนของใคร
        {
          stack: [
            { text: item.address_number || "-" },
            { text: item.full_name || "-", fontSize: 9, color: "#555555" },
          ],
        },
        { text: item.current_gauge_number || "0", alignment: "center" },
        { text: item.last_gauge_number || "0", alignment: "center" },
        { text: Number(item.unit_used || 0), alignment: "center" },
        { text: toFixed(item.water_bill || 0), alignment: "right" },
        { text: toFixed(item.equipment_maintenance_fee || 0), alignment: "right" },
        { text: toFixed(item.common_expenses || 0), alignment: "right" },
        { text: toFixed(item.waste_cost || 0), alignment: "right" },
        { text: toFixed(other), alignment: "right" },
        { text: item.count_installment || "0", alignment: "center" },
        { text: toFixed(item.overdue_amount || 0), alignment: "right" },
        { text: toFixed(debt), alignment: "right" },
        { text: toFixed(item.meter_connect_cost_all ?? item.meter_connect_cost ?? 0), alignment: "right" },
        { text: toFixed(item.late_payment_all ?? item.late_payment ?? 0), alignment: "right" },
        { text: toFixed(totalAmt), alignment: "right" },
      ];
    });

    return [
      {
        columns: [
          { text: getStorage("project_name") || "", alignment: "left", fontSize: 14, bold: true },
          { text: `ค่าน้ำ ค่าส่วนกลางและอื่นๆประจำเดือน ${format_date(new Date(), "MMMM")} พ.ศ. ${format_date(new Date(), "YYYY")}`, alignment: "right", fontSize: 14, bold: true },
        ],
      },
      {
        columns: [
          { text: `รายงานแจ้งหนี้ค่าใช้จ่ายส่วนกลาง อาคาร ${bd}`, alignment: "left", fontSize: 18, bold: true },
          { text: `พิมพ์ ณ. วันที่ ${format_date(new Date(), "DD MMMM YYYY")}`, alignment: "right", fontSize: 14, bold: true },
        ],
      },
      {
        table: {
          headerRows: 1,
          widths: ["17.00%", "5.33%", "5.33%", "5.80%", "5.73%", "6.40%", "5.96%", "5.51%", "6.84%", "2.67%", "7.46%", "6.45%", "5.11%", "6.67%", "7.74%"],
          body: [
            [
              { text: "บ้านเลขที่ / ชื่อ-นามสกุล", alignment: "center" }, { text: `เลขมาตร ${current_month}`, alignment: "center" },
              { text: `เลขมาตร ${last_month}`, alignment: "center" }, { text: "จำนวน หน่วยใช้", alignment: "center" },
              { text: "ค่าน้ำ ประปา", alignment: "center" }, { text: "ค่าบริการ อุปกรณ์", alignment: "center" },
              { text: "ค่าส่วน กลาง", alignment: "center" }, { text: "ค่าขยะ", alignment: "center" },
              { text: "รายรับ อื่นๆ", alignment: "center" }, { text: "งวด", alignment: "center" },
              { text: "จำนวนเงิน ค้างชำระ", alignment: "center" }, { text: "เพิ่ม/ลดหนี้", alignment: "center" },
              { text: "ค่าต่อ มิเตอร์", alignment: "center" }, { text: "เบี้ยปรับ", alignment: "center" },
              { text: "รวมทั้งสิ้น", alignment: "center" },
            ],
            ...tableBody,
          ],
        },
        pageBreak: bd_index === buildingKeys.length - 1 ? "" : "after",
      },
    ];
  });

  const docDefinition = {
    content: text_array,
    defaultStyle: { font: "THSarabunNew", fontSize: 10 },
    pageSize: "A4",
    pageMargins: [15, 15, 15, 15],
  };

  let textDocDefinition = await replaceText(docDefinition);
  loading(false); // ปิด loading เมื่อสร้างเสร็จพร้อมเปิดเอกสาร
  
  openPdf(textDocDefinition, `รายชื่อลูกค้าที่ยังไม่โอนกรรมสิทธิ์ห้องชุด บันทึกโอนถึง วันที่ ${new Date().toISOString()}.pdf`);
}

export { report_billing };